import { useState, useCallback } from "react";
import { Fragment } from "@tiptap/pm/model";

interface User {
  id: string;
  display_name: string;
  avatar_url?: string | null;
  is_coworker?: boolean | null;
}

interface UseMentionPopoverProps {
  workspaceMembers: User[];
  userId: string | undefined;
  editorRef: React.MutableRefObject<any>;
  mentionFilter: string;
}

interface UseMentionPopoverReturn {
  showMentions: boolean;
  setShowMentions: React.Dispatch<React.SetStateAction<boolean>>;
  mentionFilter: string;
  setMentionFilter: React.Dispatch<React.SetStateAction<string>>;
  mentionIndex: number;
  setMentionIndex: React.Dispatch<React.SetStateAction<number>>;
  filteredUsers: User[];
  insertMention: (mentionUser: User) => void;
  triggerMention: () => void;
}

export function useMentionPopoverState({
  workspaceMembers,
  userId,
  editorRef,
}: UseMentionPopoverProps): UseMentionPopoverReturn {
  const [showMentions, setShowMentions] = useState(false);
  const [mentionFilterLocal, setMentionFilter] = useState("");
  const [mentionIndex, setMentionIndex] = useState(0);

  const filteredUsers = workspaceMembers.filter(
    (u) =>
      u.id !== userId &&
      u.id !== "system-bot" &&
      u.display_name.toLowerCase().includes(mentionFilterLocal.toLowerCase()),
  );

  const insertMention = useCallback(
    (mentionUser: User) => {
      const tiptapEditor = editorRef.current?.getEditor();
      if (!tiptapEditor) return;

      const { from } = tiptapEditor.state.selection;
      const deleteFrom = from - mentionFilterLocal.length - 1; // -1 for the @

      // Use raw ProseMirror API to bypass tiptap-markdown's
      // insertContentAt override which breaks JSON node specs.
      const mentionNode = tiptapEditor.schema.nodes.mention.create({
        id: mentionUser.id,
        label: mentionUser.display_name,
      });
      const spaceNode = tiptapEditor.schema.text(" ");

      tiptapEditor.view.dispatch(
        tiptapEditor.state.tr.replaceWith(
          deleteFrom,
          from,
          Fragment.from([mentionNode, spaceNode]),
        ),
      );
      tiptapEditor.commands.focus();
      setShowMentions(false);
    },
    [editorRef, mentionFilterLocal],
  );

  const triggerMention = useCallback(() => {
    editorRef.current?.insertText("@");
    setShowMentions(true);
    setMentionFilter("");
    setMentionIndex(0);
  }, [editorRef]);

  return {
    showMentions,
    setShowMentions,
    mentionFilter: mentionFilterLocal,
    setMentionFilter,
    mentionIndex,
    setMentionIndex,
    filteredUsers,
    insertMention,
    triggerMention,
  };
}
